/**
 * Kwry Shadow DOM SDK
 * 
 * Main entry point for the production Shadow DOM SDK.
 * This provides a clean API for initializing the Kwry widget
 * with Shadow DOM isolation.
 */

// Import CSS to ensure it's processed by Vite and available for extraction
import '../index.css'

// Import React and make it globally available (needed for JSX compilation)
import React from 'react'

// Make React globally available for JSX runtime
if (typeof window !== 'undefined') {
  (window as any).React = React
}

import { shadowLogger } from '../utils/logger'
import { ShadowManager } from './shadow-manager'
import { ShadowContentDetector } from './content-detector'
import { trackSDKLoaded, trackWidgetLoaded } from '../services/analytics.service'
import { VisibilityTracker } from '../services/visibility-tracker.service'
import type { ShadowEmbedConfig } from './types'

/**
 * Main Kwry Shadow DOM SDK class
 * Provides a simple API for embedding Kwry widgets with Shadow DOM isolation
 */
export class KwryShadowSDK {
  private shadowManager: ShadowManager
  private visibilityTracker: VisibilityTracker | null = null
  private initialized = false
  private destroyed = false

  constructor(
    config: Partial<ShadowEmbedConfig>,
    callbacks?: {
      onResize?: (height: number) => void
      onOpenLink?: (url: string) => void
    }
  ) {
    // Merge with defaults
    const fullConfig: ShadowEmbedConfig = {
      // Required BaseConfig properties
      projectId: config.projectId || '',
      theme: config.theme || 'light',
      hostUrl: config.hostUrl || window.location.origin,
      apiUrl: config.apiUrl || process.env.REACT_APP_API_URL || 'https://api.whtvr.ai',
      borderRadius: config.borderRadius || '8px',
      
      // Shadow DOM specific properties
      isolateStyles: config.isolateStyles ?? true,
      allowGlobalCSS: config.allowGlobalCSS ?? false,
      mountStrategy: config.mountStrategy || 'immediate',
      shadowMode: config.shadowMode || 'open',
      autoDetectContent: config.autoDetectContent ?? true,
      
      // Other properties
      ...config
    }

    // Validate required config
    if (!fullConfig.projectId) {
      throw new Error('projectId is required for Kwry Shadow DOM SDK')
    }

    this.shadowManager = new ShadowManager(fullConfig, callbacks)
    
    shadowLogger.info('KwryShadowSDK created', {
      projectId: fullConfig.projectId,
      mountStrategy: fullConfig.mountStrategy,
      isolateStyles: fullConfig.isolateStyles
    })
  }

  /**
   * Initialize the Shadow DOM widget
   * This creates the Shadow DOM, injects CSS, and mounts the React app
   */
  async init(): Promise<void> {
    if (this.initialized) {
      shadowLogger.warn('SDK already initialized')
      return
    }

    if (this.destroyed) {
      throw new Error('Cannot reinitialize destroyed SDK instance')
    }

    try {
      const projectId = this.shadowManager.getConfig().projectId
      
      // Track SDK loaded (first stage of analytics funnel)
      trackSDKLoaded(projectId, 'shadow')
      
      // Initialize Shadow DOM infrastructure
      const initResult = await this.shadowManager.init()
      
      if (!initResult.success) {
        throw initResult.error || new Error('Shadow DOM initialization failed')
      }

      // Mount the React application
      await this.shadowManager.mount()

      // Track widget loaded (second stage of analytics funnel)
      trackWidgetLoaded(projectId)

      // Initialize visibility tracking
      this.initVisibilityTracking()

      this.initialized = true
      shadowLogger.info('✅ Shadow DOM SDK initialized successfully')

    } catch (error) {
      shadowLogger.error('❌ SDK initialization failed:', error)
      throw error
    }
  }

  /**
   * Update the SDK configuration dynamically
   */
  updateConfig(newConfig: Partial<ShadowEmbedConfig>): void {
    if (!this.initialized) {
      shadowLogger.warn('Cannot update config on uninitialized SDK')
      return
    }

    this.shadowManager.updateConfig(newConfig)
    shadowLogger.info('SDK configuration updated', newConfig)
  }

  /**
   * Initialize visibility tracking for the Shadow DOM container
   */
  private initVisibilityTracking(): void {
    const container = this.shadowManager.getContainer()
    if (container) {
      this.visibilityTracker = new VisibilityTracker({
        projectId: this.shadowManager.getConfig().projectId,
        element: container
      })
      

      
      shadowLogger.debug('Visibility tracking initialized for Shadow DOM')
    }
  }

  /**
   * Destroy the widget and clean up resources
   */
  destroy(): void {
    if (this.destroyed) {
      shadowLogger.warn('SDK already destroyed')
      return
    }

    shadowLogger.info('Destroying Kwry Shadow DOM SDK...')
    
    // Clean up visibility tracker
    if (this.visibilityTracker) {
      this.visibilityTracker.destroy()
      this.visibilityTracker = null
    }
    
    this.shadowManager.destroy()
    this.initialized = false
    this.destroyed = true
    
    shadowLogger.info('✅ SDK destroyed successfully')
  }

  /**
   * Get current SDK state for debugging
   */
  getState() {
    return {
      initialized: this.initialized,
      destroyed: this.destroyed,
      shadowManager: this.shadowManager.getState()
    }
  }

  /**
   * Test content detection (useful for debugging)
   */
  testContentDetection(): void {
    ShadowContentDetector.testDetection()
  }

  /**
   * Manually trigger content re-detection
   */
  refreshContent(): void {
    if (!this.initialized) {
      shadowLogger.warn('Cannot refresh content on uninitialized SDK')
      return
    }

    shadowLogger.info('Refreshing content detection...')
    // The ShadowApp component will handle content detection internally
    // This could be enhanced to trigger a re-detection if needed
  }

  /**
   * Get widget positioning information
   */
  getWidgetPosition(): 'beginning' | 'end' | 'mixed' | undefined {
    return this.shadowManager.getConfig().widgetPosition
  }

  /**
   * Static factory method for easy initialization
   */
  static async create(
    config: Partial<ShadowEmbedConfig>,
    callbacks?: {
      onResize?: (height: number) => void
      onOpenLink?: (url: string) => void
    }
  ): Promise<KwryShadowSDK> {
    const sdk = new KwryShadowSDK(config, callbacks)
    await sdk.init()
    return sdk
  }

  /**
   * Check exposure and positioning for a project
   */
  static async checkExposureAndPositioning(projectId: string, apiUrl?: string): Promise<{
    exposed: boolean;
    position: 'beginning' | 'end' | 'mixed';
    exposure: number;
    up: number;
  }> {
    try {
      const baseUrl = apiUrl || process.env.REACT_APP_API_URL || 'https://api.whtvr.ai'
      const url = `${baseUrl}/api/sdk/projects/${projectId}/exposure`
      
      shadowLogger.debug('Checking exposure and positioning for project', { projectId, url })
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Project-ID': projectId,
          'X-SDK-Origin': window.location.origin
        }
      })

      if (!response.ok) {
        shadowLogger.warn(`Exposure check failed: ${response.status} ${response.statusText}`)
        // If exposure check fails, default to showing the widget at beginning
        return {
          exposed: true,
          position: 'beginning',
          exposure: 1,
          up: 1
        }
      }

      const result = await response.json()

      shadowLogger.info('Exposure and positioning check completed', {
        projectId,
        exposed: result.exposed,
        position: result.position,
        exposure: `${Math.round((result.exposure ?? 1) * 100)}%`,
        up: `${Math.round((result.up ?? 1) * 100)}% beginning`
      })

      return {
        exposed: result.exposed,
        position: result.position,
        exposure: result.exposure,
        up: result.up
      }
    } catch (error) {
      shadowLogger.error('Exposure and positioning check error:', error)
      // If check fails, default to showing the widget at beginning
      return {
        exposed: true,
        position: 'beginning',
        exposure: 1,
        up: 1
      }
    }
  }

  /**
   * Check exposure for a project (legacy method for backward compatibility)
   */
  static async checkExposure(projectId: string, apiUrl?: string): Promise<boolean> {
    const result = await this.checkExposureAndPositioning(projectId, apiUrl)
    return result.exposed
  }

  /**
   * Wait for browser to be idle (works for any SPA or static site)
   * Enhanced for Safari's Back/Forward Cache (BFCache) support
   */
  static waitForBrowserIdle(): Promise<void> {
    return new Promise((resolve) => {
      shadowLogger.info('🔄 Waiting for browser to be idle...', {
        userAgent: navigator.userAgent,
        readyState: document.readyState,
        url: window.location.href
      })
      
      // Check if page is already fully loaded
      if (document.readyState === 'complete') {
        shadowLogger.info('📄 Document already complete, proceeding immediately')
        resolve()
        return
      }
      
      let resolved = false
      let resolvedBy = ''
      const resolveOnce = (source: string) => {
        if (!resolved) {
          resolved = true
          resolvedBy = source
          shadowLogger.info(`✅ Browser idle resolved by: ${source}`, {
            resolvedBy,
            timestamp: Date.now(),
            readyState: document.readyState
          })
          resolve()
        } else {
          shadowLogger.debug(`🔄 Additional resolution attempt by: ${source} (already resolved by: ${resolvedBy})`)
        }
      }
      
      // Safari-specific: Listen for pageshow event which handles BFCache
      const handlePageShow = (event: PageTransitionEvent) => {
        shadowLogger.info('📱 PageShow event fired', {
          persisted: event.persisted,
          type: event.type,
          timeStamp: event.timeStamp,
          isTrusted: event.isTrusted
        })
        
        if (event.persisted) {
          shadowLogger.info('🔄 Page loaded from Safari BFCache, DOM is ready')
        } else {
          shadowLogger.info('🆕 Regular page load complete via pageshow')
        }
        
        // Clean up the listener
        window.removeEventListener('pageshow', handlePageShow)
        resolveOnce('pageshow')
      }
      
      // Primary strategy: pageshow event (handles Safari BFCache)
      shadowLogger.debug('🎯 Setting up pageshow event listener for Safari BFCache detection')
      window.addEventListener('pageshow', handlePageShow)
      
      // Fallback strategy: Modern browsers with requestIdleCallback
      if ('requestIdleCallback' in window) {
        shadowLogger.debug('⏱️ Setting up requestIdleCallback fallback')
        requestIdleCallback(() => {
          shadowLogger.info('💤 Browser is idle, content should be rendered')
          // Remove pageshow listener if it hasn't fired yet
          window.removeEventListener('pageshow', handlePageShow)
          resolveOnce('requestIdleCallback')
        }, { 
          timeout: 3000 // Maximum wait of 3 seconds
        })
      } else {
        // Fallback for older browsers
        shadowLogger.debug('🖼️ Setting up requestAnimationFrame fallback (no requestIdleCallback)')
        requestAnimationFrame(() => {
          Promise.resolve().then(() => {
            shadowLogger.info('🎨 Browser paint complete (fallback method)')
            // Remove pageshow listener if it hasn't fired yet
            window.removeEventListener('pageshow', handlePageShow)
            resolveOnce('requestAnimationFrame')
          })
        })
      }
      
      // Add a safety timeout to prevent hanging
      setTimeout(() => {
        shadowLogger.warn('⚠️ Browser idle timeout reached (5s), forcing resolution')
        window.removeEventListener('pageshow', handlePageShow)
        resolveOnce('timeout')
      }, 5000)
    })
  }

  /**
   * Auto-initialize from script tag parameters
   * Looks for the current script tag and extracts projectId and other config
   */
  static async autoInit(): Promise<KwryShadowSDK | null> {
    try {
      shadowLogger.info('Auto-initializing from script tag...')

      // Find the current script tag
      const scripts = document.querySelectorAll('script[src*="kwry"]')
      const currentScript = scripts[scripts.length - 1] as HTMLScriptElement
      
      if (!currentScript) {
        shadowLogger.warn('Could not find Kwry script tag for auto-initialization')
        return null
      }

      // Extract parameters from script URL
      const src = currentScript.src
      const url = new URL(src)
      
      const projectId = url.searchParams.get('projectId')
      if (!projectId) {
        shadowLogger.error('No projectId found in script URL for auto-initialization')
        return null
      }

      // Extract language and direction from URL parameters only
      const language = url.searchParams.get('language') as 'eng' | 'heb' | 'arb' | null
      const direction = url.searchParams.get('direction') as 'ltr' | 'rtl' | 'auto' | null
      const containerClass = url.searchParams.get('container-class')

      // Extract other config from URL parameters
      const config: Partial<ShadowEmbedConfig> = {
        projectId,
        theme: (url.searchParams.get('theme') as 'light' | 'dark' | 'auto') || 'light',
        mountStrategy: (url.searchParams.get('mountStrategy') as any) || 'immediate',
        isolateStyles: url.searchParams.get('isolateStyles') !== 'false',
        allowGlobalCSS: url.searchParams.get('allowGlobalCSS') === 'true',
        customCSS: url.searchParams.get('customCSS') || undefined,
        autoDetectContent: url.searchParams.get('autoDetectContent') !== 'false',
        containerClass: url.searchParams.get('container-class') || undefined,
        mobileContainerClass: url.searchParams.get('mobile-container-class') || undefined,
        bottomOffset: url.searchParams.get('bottom-offset') || undefined,
        language: language || undefined,
        direction: direction || undefined,
        container: containerClass ? `.${containerClass}` : undefined
      }

      shadowLogger.info('Auto-init config extracted', config)

      // Wait for browser to be idle (handles SPAs, static sites, everything)
      await KwryShadowSDK.waitForBrowserIdle()

      // Check exposure and positioning before initializing the widget
      const exposureResult = await KwryShadowSDK.checkExposureAndPositioning(config.projectId!, config.apiUrl)
      if (!exposureResult.exposed) {
        shadowLogger.info('Widget not exposed due to exposure control settings')
        return null
      }

      // Add positioning information to config
      config.widgetPosition = exposureResult.position

      // Create and initialize SDK - let the existing init logic handle finding containers
      return await KwryShadowSDK.create(config)

    } catch (error) {
      shadowLogger.error('Auto-initialization failed:', error)
      return null
    }
  }
}

// Global URL monitoring setup (only done once)
let globalUrlMonitoring = false

function setupGlobalUrlMonitoring() {
  if (globalUrlMonitoring) return
  
  shadowLogger.info('Setting up global URL monitoring...')
  
  const handleUrlChange = () => {
    shadowLogger.info('Global URL change detected', {
      url: window.location.href
    })
    
    // Destroy current instance if exists
    const currentInstance = (window as any).kwrySDKInstance
    if (currentInstance && typeof currentInstance.destroy === 'function') {
      try {
        currentInstance.destroy()
      } catch (error) {
        shadowLogger.error('Error destroying previous instance:', error)
      }
    }
    
    // Wait for browser to be idle before re-initializing
    // This ensures the new page content has rendered
    KwryShadowSDK.waitForBrowserIdle().then(() => {
      shadowLogger.info('Re-initializing SDK on new page...')
      KwryShadowSDK.autoInit().then(instance => {
        if (instance) {
          // Update global instance
          (window as any).kwrySDKInstance = instance
          shadowLogger.info('SDK reloaded successfully on new page')
        }
      })
    })
  }
  
  // Listen to browser back/forward navigation
  window.addEventListener('popstate', handleUrlChange)
  
  // Intercept pushState and replaceState (used by SPAs for navigation)
  const originalPushState = history.pushState
  const originalReplaceState = history.replaceState
  
  history.pushState = function(...args) {
    const result = originalPushState.apply(history, args)
    shadowLogger.debug('pushState called')
    handleUrlChange()
    return result
  }
  
  history.replaceState = function(...args) {
    const result = originalReplaceState.apply(history, args)
    shadowLogger.debug('replaceState called')
    handleUrlChange()
    return result
  }
  
  globalUrlMonitoring = true
  shadowLogger.info('Global URL monitoring established')
}

// Auto-initialize when script loads (if not in module context)
if (typeof window !== 'undefined' && !(window as any).KwryShadowSDK) {
  // Make SDK available globally
  (window as any).KwryShadowSDK = KwryShadowSDK

  // Set up global URL monitoring
  setupGlobalUrlMonitoring()

  // Auto-initialize if DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', async () => {
      const instance = await KwryShadowSDK.autoInit()
      if (instance) {
        // Store the instance globally for debugging and tracking
        (window as any).kwrySDKInstance = instance
        shadowLogger.info('SDK instance stored globally as window.kwrySDKInstance')
      }
    })
  } else {
    // DOM already loaded
    KwryShadowSDK.autoInit().then(instance => {
      if (instance) {
        // Store the instance globally for debugging and tracking
        (window as any).kwrySDKInstance = instance
        shadowLogger.info('SDK instance stored globally as window.kwrySDKInstance')
      }
    })
  }
}

// Export for module usage
export default KwryShadowSDK